/*
  ЦИФРОВОЙ ДИАК ЗФ — EDITABLE PANELS
  KPIs, Stakeholders, Scope — all fully editable.
*/

import React, { useMemo, useState } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus, Trash2, GripVertical, Circle, CheckCircle2, Info } from 'lucide-react';
import { type KPI, type Stakeholder, type ProjectScopeLine } from '@/store/projectStore';
import { useProjects } from '@/contexts/ProjectContext';
import { InlineText } from './InlineEdit';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

// ─── KPI Panel ────────────────────────────────────────────────────────────

interface KPIPanelProps {
  kpis: KPI[];
  onAdd: (kpi: Omit<KPI, 'id'>) => void;
  onUpdate: (id: string, patch: Partial<KPI>) => void;
  onDelete: (id: string) => void;
}

export function KPIPanel({ kpis, onAdd, onUpdate, onDelete }: KPIPanelProps) {
  const { canEdit } = useProjects();
  const ro = !canEdit;
  return (
    <div className="space-y-2">
      {kpis.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border/80 bg-muted/20 px-3 py-3.5 text-center text-[11px] leading-snug text-muted-foreground">
          Метрик пока нет. Добавьте показатель кнопкой ниже.
        </p>
      ) : (
        <ul className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
          {kpis.map((k) => (
            <li
              key={k.id}
              className="group flex flex-row items-center gap-2 border-b border-border px-2.5 py-1.5 last:border-b-0 sm:gap-3 sm:px-3 sm:py-2"
            >
              <div className="min-w-0 flex-1 pr-1">
                <InlineText
                  value={k.label}
                  onSave={(v) => onUpdate(k.id, { label: v })}
                  className="block w-full text-xs leading-tight text-foreground"
                  inputClassName="text-xs w-full min-w-0 font-normal leading-tight"
                  displayAs="span"
                  placeholder="Название показателя"
                  readOnly={ro}
                />
              </div>
              <div className="flex shrink-0 items-center gap-1 sm:gap-1.5">
                <div className="grid min-w-0 max-w-[11rem] shrink-0 grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-1.5 rounded-md bg-muted/45 px-2 py-1 tabular-nums ring-1 ring-border/50 sm:max-w-[12.5rem] sm:min-w-[10rem]">
                  <InlineText
                    value={k.value}
                    onSave={(v) => onUpdate(k.id, { value: v })}
                    className="min-w-0 text-right text-sm font-semibold tracking-tight text-[var(--nn-blue)]"
                    inputClassName="text-sm font-semibold w-full min-w-0 max-w-full text-right tabular-nums leading-tight"
                    displayAs="span"
                    readOnly={ro}
                  />
                  <InlineText
                    value={k.unit}
                    onSave={(v) => onUpdate(k.id, { unit: v })}
                    className="max-w-[4.5rem] shrink-0 text-left text-[10px] font-medium tabular-nums text-muted-foreground"
                    inputClassName="text-[10px] w-full min-w-0 max-w-[4.5rem] leading-tight"
                    placeholder="—"
                    displayAs="span"
                    readOnly={ro}
                  />
                </div>
                {!ro ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (typeof window !== 'undefined') {
                        const label = k.label?.trim() || 'без названия';
                        const ok = window.confirm(`Удалить метрику «${label}»? Действие необратимо.`);
                        if (!ok) return;
                      }
                      onDelete(k.id);
                    }}
                    className="shrink-0 rounded-md p-1 text-muted-foreground opacity-70 transition-all hover:bg-destructive/10 hover:text-destructive hover:opacity-100 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:opacity-0 sm:group-hover:opacity-100"
                    aria-label="Удалить метрику"
                  >
                    <Trash2 size={13} strokeWidth={2} />
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
      {!ro ? (
        <button
          type="button"
          onClick={() => onAdd({ label: 'Новая метрика', value: '—', unit: '' })}
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-border py-1.5 text-[11px] font-medium text-muted-foreground transition-colors hover:border-[var(--nn-blue)] hover:bg-[var(--nn-blue-pale)]/40 hover:text-[var(--nn-blue)] sm:w-auto sm:justify-start sm:px-2.5 sm:py-1.5"
        >
          <Plus size={13} strokeWidth={2} />
          Добавить метрику
        </button>
      ) : null}
    </div>
  );
}

// ─── Stakeholder Panel ────────────────────────────────────────────────────

const INVOLVEMENT_OPTIONS = [
  { value: 'responsible', label: 'Ответственный (R)' },
  { value: 'accountable', label: 'Утверждающий (A)' },
  { value: 'consulted', label: 'Консультируемый (C)' },
  { value: 'informed', label: 'Информируемый (I)' },
] as const;

const INVOLVEMENT_BADGE: Record<Stakeholder['involvement'], { label: string; className: string }> = {
  responsible: { label: 'R', className: 'bg-blue-500/15 text-blue-700 dark:text-blue-300' },
  accountable: { label: 'A', className: 'bg-violet-500/15 text-violet-700 dark:text-violet-300' },
  consulted: { label: 'C', className: 'bg-amber-500/15 text-amber-800 dark:text-amber-200' },
  informed: { label: 'I', className: 'bg-muted text-muted-foreground' },
};

/** Обводка для легенды — не дублирует вид «плашек» в списке участников. */
const LEGEND_LETTER_RING: Record<Stakeholder['involvement'], string> = {
  responsible: 'ring-blue-500/45 bg-blue-500/[0.07] text-blue-800 dark:text-blue-200',
  accountable: 'ring-violet-500/45 bg-violet-500/[0.07] text-violet-800 dark:text-violet-200',
  consulted: 'ring-amber-500/45 bg-amber-500/[0.07] text-amber-900 dark:text-amber-100',
  informed: 'ring-border bg-muted/50 text-muted-foreground',
};

function StakeholderInvolvementSelect({
  value,
  onChange,
  readOnly,
}: {
  value: Stakeholder['involvement'];
  onChange: (v: Stakeholder['involvement']) => void;
  readOnly: boolean;
}) {
  const b = INVOLVEMENT_BADGE[value];
  const fullLabel = INVOLVEMENT_OPTIONS.find((o) => o.value === value)?.label ?? 'Роль RACI';
  if (readOnly) {
    return (
      <span
        className={cn(
          'inline-flex h-7 min-w-[1.75rem] cursor-default items-center justify-center rounded-md px-1.5 text-[10px] font-bold tabular-nums ring-1 ring-border/60',
          b.className,
        )}
        title={fullLabel}
      >
        {b.label}
      </span>
    );
  }
  return (
    <Select value={value} onValueChange={(v) => onChange(v as Stakeholder['involvement'])}>
      <SelectTrigger
        size="sm"
        className={cn(
          'h-7 w-9 min-w-9 max-w-9 shrink-0 justify-center gap-0 overflow-hidden border px-0 shadow-xs [&_svg]:size-3',
          'ring-1 ring-border/60 focus-visible:border-[var(--nn-blue)] focus-visible:ring-2 focus-visible:ring-[var(--nn-blue-light)]/50',
          b.className,
        )}
        aria-label={fullLabel}
      >
        <SelectValue>
          <span className="text-[10px] font-bold tabular-nums">{b.label}</span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent
        position="popper"
        align="start"
        sideOffset={6}
        className="z-[100] max-h-72 w-[min(18rem,calc(100vw-2rem))]"
      >
        {INVOLVEMENT_OPTIONS.map((o) => (
          <SelectItem key={o.value} value={o.value} className="cursor-pointer py-2 pr-8 pl-2 text-sm">
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const RACI_LEGEND: { involvement: Stakeholder['involvement']; title: string; hint: string }[] = [
  {
    involvement: 'responsible',
    title: 'Ответственный (R)',
    hint: 'непосредственно выполняет работу по задачам и отчитывается о ходе и результате',
  },
  {
    involvement: 'accountable',
    title: 'Утверждающий (A)',
    hint: 'несёт итоговую ответственность за результат, принимает финальные решения при спорах',
  },
  {
    involvement: 'consulted',
    title: 'Консультируемый (C)',
    hint: 'подключают за мнением до решения; экспертный вклад учитывают при выборе варианта',
  },
  {
    involvement: 'informed',
    title: 'Информируемый (I)',
    hint: 'получает сведения о ходе и решениях, без обязанности согласовывать каждый шаг',
  },
];

function RaciLegend() {
  return (
    <div
      className="rounded-xl border border-dashed border-border/90 bg-gradient-to-b from-muted/40 to-muted/20 px-4 py-3.5 shadow-none"
      role="note"
      aria-label="Расшифровка ролей RACI"
    >
      <div className="mb-3 flex items-start gap-2.5">
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-background/80 text-[var(--nn-blue)] ring-1 ring-border/70">
          <Info size={15} strokeWidth={2} aria-hidden />
        </span>
        <div className="min-w-0 pt-0.5">
          <p className="text-xs font-semibold leading-tight text-foreground">Справка: модель RACI</p>
          <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
            Ниже — расшифровка букв. В списке участников выберите код роли для каждой строки.
          </p>
        </div>
      </div>
      <ul className="grid gap-2.5 sm:grid-cols-2 sm:gap-x-4 sm:gap-y-2.5">
        {RACI_LEGEND.map(({ involvement, title, hint }) => {
          const b = INVOLVEMENT_BADGE[involvement];
          const ring = LEGEND_LETTER_RING[involvement];
          return (
            <li key={involvement} className="flex gap-2.5 text-[11px] leading-snug">
              <span
                className={cn(
                  'mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold tabular-nums ring-2 ring-inset',
                  ring,
                )}
                aria-hidden
              >
                {b.label}
              </span>
              <span className="min-w-0 text-muted-foreground">
                <span className="font-medium text-foreground">{title}</span>
                <span className="mt-0.5 block text-[10px] leading-relaxed opacity-90">{hint}</span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

interface StakeholderPanelProps {
  stakeholders: Stakeholder[];
  onAdd: (sh: Omit<Stakeholder, 'id'>) => void;
  onUpdate: (id: string, patch: Partial<Stakeholder>) => void;
  onDelete: (id: string) => void;
}

const SH_GRID =
  'grid grid-cols-1 gap-3 sm:grid-cols-[2.75rem_minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-center sm:gap-x-3';

export function StakeholderPanel({ stakeholders, onAdd, onUpdate, onDelete }: StakeholderPanelProps) {
  const { canEdit } = useProjects();
  const ro = !canEdit;
  return (
    <div className="space-y-4">
      <RaciLegend />

      <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
        <div className="border-b border-border bg-muted/30 px-3 py-2.5 sm:px-4">
          <p className="text-xs font-semibold text-foreground">Участники проекта</p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            Код RACI, подразделение или ФИО, затем роль в этом проекте.
          </p>
        </div>

        {stakeholders.length === 0 ? (
          <p className="px-3 py-4 text-xs text-muted-foreground sm:px-4">
            Пока никого нет — добавьте участника кнопкой ниже.
          </p>
        ) : (
          <>
            <div
              className={cn(
                SH_GRID,
                'hidden border-b border-border/80 bg-muted/15 px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground sm:grid sm:px-4',
              )}
            >
              <span className="text-center">Код</span>
              <span>Участник</span>
              <span>Роль в проекте</span>
              <span className="w-8" aria-hidden />
            </div>
            <ul className="divide-y divide-border/80">
              {stakeholders.map((sh) => (
                <li
                  key={sh.id}
                  className="group px-3 py-3 transition-colors hover:bg-muted/25 sm:px-4 sm:py-2.5"
                >
                  <div className={SH_GRID}>
                    <div className="flex items-center justify-between gap-2 sm:block sm:justify-start">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground sm:hidden">
                        Код RACI
                      </span>
                      <div className="flex justify-end sm:justify-center">
                        <StakeholderInvolvementSelect
                          value={sh.involvement}
                          onChange={(v) => onUpdate(sh.id, { involvement: v })}
                          readOnly={ro}
                        />
                      </div>
                    </div>
                    <div className="min-w-0">
                      <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground sm:hidden">
                        Участник
                      </span>
                      <InlineText
                        value={sh.name}
                        onSave={(v) => onUpdate(sh.id, { name: v })}
                        className="text-xs font-medium leading-snug text-foreground"
                        inputClassName="text-xs w-full min-w-0 font-medium"
                        displayAs="span"
                        placeholder="Подразделение / ФИО"
                        readOnly={ro}
                      />
                    </div>
                    <div className="min-w-0">
                      <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground sm:hidden">
                        Роль в проекте
                      </span>
                      <InlineText
                        value={sh.role}
                        onSave={(v) => onUpdate(sh.id, { role: v })}
                        className="text-xs leading-snug text-foreground"
                        inputClassName="text-xs w-full min-w-0"
                        displayAs="span"
                        placeholder="Например: владелец продукта, заказчик…"
                        readOnly={ro}
                      />
                    </div>
                    {!ro ? (
                      <div className="flex justify-end sm:justify-center">
                        <button
                          type="button"
                          onClick={() => {
                            if (typeof window !== 'undefined') {
                              const label = sh.name?.trim() || 'без имени';
                              const ok = window.confirm(`Удалить стейкхолдера «${label}»? Действие необратимо.`);
                              if (!ok) return;
                            }
                            onDelete(sh.id);
                          }}
                          className="rounded-md p-1.5 text-muted-foreground opacity-70 transition-all hover:bg-destructive/10 hover:text-destructive hover:opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-within:opacity-100"
                          aria-label="Удалить стейкхолдера"
                        >
                          <Trash2 size={14} strokeWidth={2} />
                        </button>
                      </div>
                    ) : (
                      <span className="hidden sm:block" aria-hidden />
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {!ro ? (
        <button
          type="button"
          onClick={() => onAdd({ name: 'Новый стейкхолдер', role: '', involvement: 'informed' })}
          className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-border px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:border-[var(--nn-blue)] hover:bg-[var(--nn-blue-pale)]/40 hover:text-[var(--nn-blue)]"
        >
          <Plus size={14} strokeWidth={2} />
          Добавить участника
        </button>
      ) : null}
    </div>
  );
}

// ─── Scope Panel ──────────────────────────────────────────────────────────

/** Вертикальное совмещение с серединой первой строки текста (text-sm + leading-snug на <li>). */
const SCOPE_ROW_TEXT = 'text-sm leading-snug';
const SCOPE_GRIP_BULLET_PAD = 'pt-[calc(0.5lh-7px)]'; /* 7px ≈ половина высоты GripVertical 14px */
const SCOPE_BULLET_ONLY_PAD = 'pt-[calc(0.5lh-3px)]'; /* 3px ≈ половина диаметра буллита 6px */
const SCOPE_DELETE_PAD = 'pt-[calc(0.5lh-6px)]'; /* 6px ≈ половина иконки корзины 12px */
const SCOPE_DONE_ICON_PAD = 'pt-[calc(0.5lh-8px)]'; /* 8px ≈ половина иконки 16px */
const SCOPE_DONE_TEXT = 'text-[#00A651] line-through decoration-[#00A651] decoration-[1.5px]';

function ScopeDoneControl({
  done,
  canToggle,
  onToggle,
}: {
  done: boolean;
  canToggle: boolean;
  onToggle?: () => void;
}) {
  if (canToggle && onToggle) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        className={cn(
          'self-start shrink-0 rounded-full p-1 transition-colors',
          SCOPE_DONE_ICON_PAD,
          done
            ? 'text-[#00A651]'
            : 'text-muted-foreground hover:bg-green-50 hover:text-[#00A651]/90',
        )}
        aria-label={done ? 'Снять отметку о выполнении' : 'Отметить пункт как выполненный'}
        aria-pressed={done}
      >
        {done ? (
          <CheckCircle2 className="h-4 w-4" strokeWidth={2} aria-hidden />
        ) : (
          <Circle className="h-4 w-4" strokeWidth={2} aria-hidden />
        )}
      </button>
    );
  }
  if (done) {
    return (
      <span
        className={cn('pointer-events-none self-start shrink-0 text-[#00A651]', SCOPE_DONE_ICON_PAD)}
        title="Выполнено"
        aria-hidden
      >
        <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
      </span>
    );
  }
  return null;
}

function SortableScopeRow({
  id,
  index,
  line,
  onUpdate,
  onDelete,
  onToggleDone,
}: {
  id: string;
  index: number;
  line: ProjectScopeLine;
  onUpdate: (idx: number, text: string) => void;
  onDelete: (idx: number) => void;
  onToggleDone?: () => void;
}) {
  const {
    setNodeRef,
    setActivatorNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const done = Boolean(line.done);
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        'group flex items-start gap-2 rounded-md',
        SCOPE_ROW_TEXT,
        isDragging && 'relative z-10 bg-muted/50 shadow-md',
      )}
    >
      <div
        className={cn(
          'flex shrink-0 items-center gap-0.5 self-start',
          SCOPE_GRIP_BULLET_PAD,
        )}
      >
        <button
          type="button"
          ref={setActivatorNodeRef}
          {...listeners}
          {...attributes}
          className="flex-shrink-0 touch-none rounded p-0.5 text-muted-foreground hover:bg-muted cursor-grab active:cursor-grabbing"
          aria-label="Переместить пункт"
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>
        <span
          className={cn(
            'h-1.5 w-1.5 shrink-0 rounded-full',
            done ? 'bg-[#00A651]' : 'bg-[var(--nn-blue)]',
          )}
          aria-hidden
        />
      </div>
      <div className="min-h-[1lh] min-w-0 flex-1">
        <InlineText
          value={line.text}
          onSave={(v) => onUpdate(index, v)}
          multiline
          className={cn('w-full text-sm leading-snug', done && SCOPE_DONE_TEXT)}
          displayAs="span"
          placeholder="Элемент содержания…"
          readOnly={false}
        />
      </div>
      <ScopeDoneControl done={done} canToggle={Boolean(onToggleDone)} onToggle={onToggleDone} />
      <button
        type="button"
        onClick={() => {
          if (typeof window !== 'undefined') {
            const label = line.text?.trim() || 'пустой пункт';
            const preview = label.length > 60 ? `${label.slice(0, 60)}…` : label;
            const ok = window.confirm(`Удалить пункт scope «${preview}»? Действие необратимо.`);
            if (!ok) return;
          }
          onDelete(index);
        }}
        className={cn(
          'opacity-0 group-hover:opacity-100 self-start p-1 rounded text-red-400 hover:text-red-600 hover:bg-red-50 transition-all shrink-0',
          SCOPE_DELETE_PAD,
        )}
        aria-label="Удалить пункт"
      >
        <Trash2 size={12} />
      </button>
    </li>
  );
}

interface ScopePanelProps {
  scope: ProjectScopeLine[];
  onAdd: (text: string) => void;
  onUpdate: (idx: number, text: string) => void;
  onDelete: (idx: number) => void;
  onReorder?: (oldIndex: number, newIndex: number) => void;
  onToggleDone?: (idx: number) => void;
}

export function ScopePanel({ scope, onAdd, onUpdate, onDelete, onReorder, onToggleDone }: ScopePanelProps) {
  const { canEdit } = useProjects();
  const ro = !canEdit;
  const sortable = !ro && Boolean(onReorder);
  const ids = useMemo(() => scope.map((_, i) => String(i)), [scope]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const onDragEnd = (e: DragEndEvent) => {
    if (!onReorder) return;
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(oldIndex, newIndex);
  };

  const listBody = scope.map((line, idx) => {
    const id = String(idx);
    const done = Boolean(line.done);
    const toggle = onToggleDone ? () => onToggleDone(idx) : undefined;
    if (sortable) {
      return (
        <SortableScopeRow
          key={id}
          id={id}
          index={idx}
          line={line}
          onUpdate={onUpdate}
          onDelete={onDelete}
          onToggleDone={toggle}
        />
      );
    }
    return (
      <li key={id} className={cn('group flex items-start gap-2', SCOPE_ROW_TEXT)}>
        <div className={cn('flex shrink-0 items-center self-start', SCOPE_BULLET_ONLY_PAD)}>
          <span
            className={cn(
              'h-1.5 w-1.5 shrink-0 rounded-full',
              done ? 'bg-[#00A651]' : 'bg-[var(--nn-blue)]',
            )}
            aria-hidden
          />
        </div>
        <div className="min-h-[1lh] min-w-0 flex-1">
          <InlineText
            value={line.text}
            onSave={(v) => onUpdate(idx, v)}
            multiline
            className={cn('w-full text-sm leading-snug', done && SCOPE_DONE_TEXT)}
            displayAs="span"
            placeholder="Элемент содержания…"
            readOnly={ro}
          />
        </div>
        <ScopeDoneControl done={done} canToggle={!ro && Boolean(onToggleDone)} onToggle={toggle} />
        {!ro ? (
          <button
            type="button"
            onClick={() => {
              if (typeof window !== 'undefined') {
                const label = line.text?.trim() || 'пустой пункт';
                const preview = label.length > 60 ? `${label.slice(0, 60)}…` : label;
                const ok = window.confirm(`Удалить пункт scope «${preview}»? Действие необратимо.`);
                if (!ok) return;
              }
              onDelete(idx);
            }}
            className={cn(
              'shrink-0 self-start rounded p-1 text-red-400 opacity-0 transition-all hover:bg-red-50 hover:text-red-600 group-hover:opacity-100',
              SCOPE_DELETE_PAD,
            )}
            aria-label="Удалить пункт"
          >
            <Trash2 size={12} />
          </button>
        ) : null}
      </li>
    );
  });

  const list = <ul className="space-y-1.5 mb-3">{listBody}</ul>;

  return (
    <div>
      {sortable ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            {list}
          </SortableContext>
        </DndContext>
      ) : (
        list
      )}
      {!ro ? (
        <button
          type="button"
          onClick={() => onAdd('Новый элемент содержания')}
          className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border border-dashed border-[var(--nn-blue)] text-[var(--nn-blue)] hover:bg-[var(--nn-blue-pale)] transition-colors"
        >
          <Plus size={13} /> Добавить элемент
        </button>
      ) : null}
    </div>
  );
}
